import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Seo, { trimMetaDescription } from '../components/Seo';
import CourseIcon from '../components/CourseIcon';
import { getCourseById } from '../services/courses';
import './CourseDetail.css';

const CourseDetail = () => {
  const { id }      = useParams();
  const navigate     = useNavigate();
  const [course,          setCourse]          = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [expandedModule,  setExpandedModule]  = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCourseById(id)
      .then((res) => {
        if (!cancelled) {
          if (res.data?.success && res.data?.data) {
            setCourse(res.data.data);
          } else {
            navigate('/courses', { replace: true });
          }
        }
      })
      .catch(() => { if (!cancelled) navigate('/courses', { replace: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, navigate]);

  const toggleModule = (index) => setExpandedModule(expandedModule === index ? null : index);

  if (loading) {
    return (
      <div className="pd-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <div style={{ color: 'var(--text-2)', fontSize: 15 }}>Loading course…</div>
      </div>
    );
  }

  if (!course) return null;

  const totalSessions = (course.modules || []).reduce((sum, mod) => sum + (mod.sessions || []).length, 0);

  return (
    <div className="pd-page">
      <Seo
        title={course.title}
        description={trimMetaDescription(`${course.tagline} ${course.overview}`)}
      />

      {/* Hero */}
      <section className="pd-hero">
        <div className="pd-hero-pattern" />
        <div className="pd-container">
          <Link to="/courses" className="pd-back-link">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13 8H3M7 12l-4-4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            All Courses
          </Link>

          <div className="pd-hero-content">
            <div className="pd-hero-left">
              <div className="pd-title-row">
                <div className="pd-course-badge">
                  <CourseIcon icon={course.icon} className="pd-course-icon" />
                </div>
                <h1 className="pd-hero-title">{course.title}</h1>
              </div>
              <p className="pd-hero-tagline">{course.tagline}</p>
              <p className="pd-hero-overview">{course.overview}</p>

              <div className="pd-meta-strip">
                <div className="pd-meta-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M9 5.5V9l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span><strong>{course.duration}</strong></span>
                </div>
                <div className="pd-meta-divider" />
                <div className="pd-meta-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9 2a4 4 0 1 1 0 8A4 4 0 0 1 9 2zM2 16a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>{course.target_audience}</span>
                </div>
                {totalSessions > 0 && (
                  <>
                    <div className="pd-meta-divider" />
                    <div className="pd-meta-item">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <rect x="2" y="4" width="14" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M6 4V2.5M12 4V2.5M2 8h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span><strong>{totalSessions}</strong> Sessions across <strong>{course.modules.length}</strong> Modules</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="pd-hero-right">
              <div className="pd-outcomes-card">
                <div className="pd-outcomes-header">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2l2.4 5.1 5.6.5-4 4 1 5.4L10 14.5 5 17l1-5.4-4-4 5.6-.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                  <h3>What You Will Achieve</h3>
                </div>
                <ul className="pd-outcomes-list">
                  {(course.outcomes || []).map((outcome, idx) => (
                    <li key={idx} className="pd-outcome-item">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M15 4.5L7 13.5 3 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="pd-enrol-btn">
                  Enquire About This Course
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      {course.modules && course.modules.length > 0 && (
        <section className="pd-curriculum">
          <div className="pd-container">
            <div className="pd-section-header">
              <h2 className="pd-section-title">Full Curriculum</h2>
              <p className="pd-section-subtitle">Session-by-session breakdown of everything covered in this course</p>
            </div>

            <div className="pd-modules">
              {course.modules.map((mod, modIdx) => (
                <div key={modIdx} className={`pd-module ${expandedModule === modIdx ? 'pd-module--open' : ''}`}>
                  <button className="pd-module-header" onClick={() => toggleModule(modIdx)} aria-expanded={expandedModule === modIdx}>
                    <div className="pd-module-header-left">
                      <div className="pd-module-index">{modIdx + 1}</div>
                      <div className="pd-module-meta">
                        <span className="pd-module-title">{mod.title}</span>
                        <span className="pd-module-session-count">
                          {mod.sessions.length} {mod.sessions.length === 1 ? 'session' : 'sessions'}
                        </span>
                      </div>
                    </div>
                    <div className={`pd-module-chevron ${expandedModule === modIdx ? 'pd-module-chevron--open' : ''}`}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </button>

                  <div className="pd-module-body">
                    <div className="pd-sessions">
                      {mod.sessions.map((session, sessIdx) => (
                        <div key={sessIdx} className="pd-session">
                          <div className="pd-session-header">
                            <div className="pd-session-number">Session {session.number}</div>
                            <div className="pd-session-duration">
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.25"/>
                                <path d="M7 4.5V7l1.75 1.05" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
                              </svg>
                              {session.duration}
                            </div>
                          </div>
                          <h4 className="pd-session-title">{session.title}</h4>
                          <ul className="pd-session-topics">
                            {(session.topics || []).map((topic, topicIdx) => (
                              <li key={topicIdx} className="pd-topic-item">
                                <span className="pd-topic-dot" />
                                {topic}
                              </li>
                            ))}
                          </ul>
                          {session.note && (
                            <div className="pd-session-note">
                              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.25"/>
                                <path d="M7.5 7v3M7.5 5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              {session.note}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pd-cta">
        <div className="pd-cta-pattern" />
        <div className="pd-container pd-cta-inner">
          <div className="pd-cta-text">
            <h2>Ready to Start Learning?</h2>
            <p>Reach out to us to know more about batch schedules, fees, and enrolment.</p>
          </div>
          <div className="pd-cta-actions">
            <Link to="/contact" className="pd-cta-btn-primary">Contact Us</Link>
            <Link to="/courses" className="pd-cta-btn-secondary">Browse All Courses</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
